import { produce } from 'immer';
import type { PlayerId, RoundId, Session, SessionEvent } from '@/schemas';
import { SessionSchema } from '@/schemas';
import { getReplayableSessionEvents } from './event-activity';
import {
  handlePlayerEvent,
  handleRoundEvent,
  handleScoreEvent,
  handleSpectatorEvent,
  handleInviteTokenEvent,
  handleJoinRequestEvent,
  handleVisibilityEvent,
  handleImageEvent,
} from './event-handlers';

const noopHandler = () => {};

/**
 * Helper to create visibility event handler
 */
function createVisibilityEventHandler(draft: Session) {
  return (event: SessionEvent) => {
    if (
      event.type === 'SessionPublicSpectatorEnabled' ||
      event.type === 'SessionPublicSpectatorDisabled' ||
      event.type === 'SessionPublicPlayerJoinEnabled' ||
      event.type === 'SessionPublicPlayerJoinDisabled'
    ) {
      handleVisibilityEvent(draft, event);
    }
  };
}

/**
 * Helper to resolve round index by ID with caching
 */
function resolveRoundIndexHelper(
  roundId: RoundId,
  rounds: Session['rounds'],
  roundIndexById: Map<RoundId, number>,
): number | undefined {
  const mappedIndex = roundIndexById.get(roundId);
  if (mappedIndex !== undefined && rounds[mappedIndex]?.id === roundId) {
    return mappedIndex;
  }
  const nextIndex = rounds.findIndex((round) => round.id === roundId);
  if (nextIndex !== -1) {
    roundIndexById.set(roundId, nextIndex);
    return nextIndex;
  }
  return undefined;
}

/**
 * Helper to resolve player index by ID with caching and mapping support
 */
function resolvePlayerIndexHelper(
  playerId: PlayerId,
  players: Session['players'],
  playerIndexById: Map<PlayerId, number>,
  resolveMappedPlayerId: (id: PlayerId) => PlayerId,
): number | undefined {
  const resolvedId = resolveMappedPlayerId(playerId);
  const candidates: PlayerId[] = resolvedId === playerId ? [playerId] : [resolvedId, playerId];

  for (const candidate of candidates) {
    const mappedIndex = playerIndexById.get(candidate);
    if (mappedIndex !== undefined && players[mappedIndex]?.id === candidate) {
      playerIndexById.set(playerId, mappedIndex);
      return mappedIndex;
    }
  }

  for (const candidate of candidates) {
    const nextIndex = players.findIndex((player) => player.id === candidate);
    if (nextIndex !== -1) {
      playerIndexById.set(candidate, nextIndex);
      playerIndexById.set(playerId, nextIndex);
      return nextIndex;
    }
  }

  return undefined;
}

/**
 * Helper to process a single event using the provided handler map
 */
function processEventHandler(
  event: SessionEvent,
  handlerByType: Partial<Record<SessionEvent['type'], (event: SessionEvent) => void>>,
): void {
  // Skip SessionStarted - we already have a session (in snapshot replay) or it's handled separately (in full replay)
  if (event.type === 'SessionStarted') {
    return;
  }

  const handler = handlerByType[event.type];
  if (!handler) {
    console.warn('Unknown event type:', event);
    return;
  }

  handler(event);
}

/**
 * Replay delta events on top of an existing session snapshot
 *
 * @remarks
 * This function applies delta events (events after a snapshot) to an existing session state.
 * It reuses the same event handlers as full replay but starts from the snapshot session
 * instead of building from SessionStarted.
 *
 * @param snapshotSession - Session state from snapshot
 * @param deltaEvents - Events to apply on top of the snapshot
 * @returns Updated Session with delta events applied
 * @throws Error if replayed session is invalid
 */
export const replaySessionFromSnapshot = (
  snapshotSession: Session,
  deltaEvents: SessionEvent[],
): Session => {
  const { events: replayableEvents } = getReplayableSessionEvents(deltaEvents);

  // If no events to replay, return snapshot as-is
  if (replayableEvents.length === 0) {
    return snapshotSession;
  }

  // Build index maps from snapshot session for efficient lookups
  const roundIndexById = new Map<RoundId, number>();
  const playerIndexById = new Map<PlayerId, number>();

  // Initialize indices from snapshot session
  for (const [index, round] of snapshotSession.rounds.entries()) {
    roundIndexById.set(round.id, index);
  }

  for (const [index, player] of snapshotSession.players.entries()) {
    playerIndexById.set(player.id, index);
  }

  // Resolve mapped player IDs (for player merges)
  // Note: In snapshot replay, player mappings are already applied in the snapshot,
  // so this function is simplified (PlayerIdMapped events are handled by falling back to full replay)
  const resolveMappedPlayerId = (playerId: PlayerId): PlayerId => {
    // PlayerIdMapped/PlayerIdUnmapped events cause fallback to full replay,
    // so we can just return the playerId as-is (mappings already applied in snapshot)
    return playerId;
  };

  // Reuse the same event handling logic from full replay
  // We'll use a simplified version that applies events to the existing session
  const replayedSession = produce(snapshotSession, (draft) => {
    const resolveRoundIndex = (roundId: RoundId): number | undefined =>
      resolveRoundIndexHelper(roundId, draft.rounds, roundIndexById);

    const resolvePlayerIndex = (playerId: PlayerId): number | undefined =>
      resolvePlayerIndexHelper(playerId, draft.players, playerIndexById, resolveMappedPlayerId);

    // Handle session-level events
    const handleSessionEvent = (draft: Session, event: SessionEvent): void => {
      switch (event.type) {
        case 'SessionNameChanged': {
          draft.name = event.payload.name;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionNotesChanged': {
          draft.notes = event.payload.notes;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionGameConfigChanged': {
          draft.gameConfigSnapshot = event.payload.gameConfigSnapshot;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionTagAdded': {
          if (!draft.tags) {
            draft.tags = [];
          }
          if (!draft.tags.includes(event.payload.tag)) {
            draft.tags.push(event.payload.tag);
          }
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionTagRemoved': {
          draft.tags = draft.tags?.filter((tag) => tag !== event.payload.tag);
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionPaused': {
          draft.status = 'paused';
          draft.pausedAt = event.timestamp;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionResumed': {
          draft.status = 'ongoing';
          draft.pausedAt = undefined;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionRestarted': {
          // Reset session state for restart
          draft.status = 'ongoing';
          draft.pausedAt = undefined;
          draft.endedAt = undefined;
          draft.currentRoundNumber = 0;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionArchived': {
          draft.archivedAt = event.payload.archivedAt ?? event.timestamp;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionUnarchived': {
          draft.archivedAt = undefined;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionEnded': {
          draft.status = 'finished';
          draft.endedAt = event.timestamp;
          draft.pausedAt = undefined;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionWinnerSet': {
          draft.winner =
            event.payload.winnerIds.length === 1 ? event.payload.winnerIds[0] : undefined;
          draft.updatedAt = event.timestamp;
          break;
        }
        case 'SessionOwnershipClaimed': {
          // Ownership is not stored in Session schema, skip
          draft.updatedAt = event.timestamp;
          break;
        }
        default: {
          // Not a session-level event
          break;
        }
      }
    };

    const sessionEventHandler = (event: SessionEvent) => handleSessionEvent(draft, event);
    const playerEventHandler = (event: SessionEvent) =>
      handlePlayerEvent(draft, event, resolvePlayerIndex, playerIndexById);
    const roundEventHandler = (event: SessionEvent) =>
      handleRoundEvent(draft, event, resolveRoundIndex, roundIndexById);
    const scoreEventHandler = (event: SessionEvent) =>
      handleScoreEvent(draft, event, resolveRoundIndex, resolveMappedPlayerId);
    const spectatorEventHandler = (event: SessionEvent) => handleSpectatorEvent(draft, event);
    const inviteTokenEventHandler = (event: SessionEvent) => handleInviteTokenEvent(draft, event);
    const joinRequestEventHandler = (event: SessionEvent) => handleJoinRequestEvent(draft, event);
    const visibilityEventHandler = createVisibilityEventHandler(draft);
    const imageEventHandler = (event: SessionEvent) => handleImageEvent(draft, event);

    const handlerByType: Partial<Record<SessionEvent['type'], (event: SessionEvent) => void>> = {
      SessionNameChanged: sessionEventHandler,
      SessionNotesChanged: sessionEventHandler,
      SessionGameConfigChanged: sessionEventHandler,
      SessionTagAdded: sessionEventHandler,
      SessionTagRemoved: sessionEventHandler,
      SessionPaused: sessionEventHandler,
      SessionResumed: sessionEventHandler,
      SessionRestarted: sessionEventHandler,
      SessionArchived: sessionEventHandler,
      SessionUnarchived: sessionEventHandler,
      SessionEnded: sessionEventHandler,
      SessionWinnerSet: sessionEventHandler,
      SessionOwnershipClaimed: sessionEventHandler,
      PlayerJoined: playerEventHandler,
      PlayerLeft: playerEventHandler,
      PlayerRemoved: playerEventHandler,
      PlayerOrderChanged: playerEventHandler,
      SpectatorJoined: spectatorEventHandler,
      SpectatorLeft: spectatorEventHandler,
      RoundStarted: roundEventHandler,
      RoundCompleted: roundEventHandler,
      RoundNoteAdded: roundEventHandler,
      RoundNoteRemoved: roundEventHandler,
      RoundTagAdded: roundEventHandler,
      RoundTagRemoved: roundEventHandler,
      RoundDeleted: roundEventHandler,
      ScoreChanged: scoreEventHandler,
      ScoreSet: scoreEventHandler,
      InviteTokenGenerated: inviteTokenEventHandler,
      InviteTokenRevoked: inviteTokenEventHandler,
      InviteLinkEnabled: inviteTokenEventHandler,
      InviteLinkDisabled: inviteTokenEventHandler,
      SessionPublicSpectatorEnabled: visibilityEventHandler,
      SessionPublicSpectatorDisabled: visibilityEventHandler,
      SessionPublicPlayerJoinEnabled: visibilityEventHandler,
      SessionPublicPlayerJoinDisabled: visibilityEventHandler,
      JoinRequestCreated: joinRequestEventHandler,
      JoinRequestApproved: joinRequestEventHandler,
      JoinRequestRejected: joinRequestEventHandler,
      ImageAdded: imageEventHandler,
      ImageRemoved: imageEventHandler,
      ImageCaptionChanged: imageEventHandler,
      ImagePrimarySet: imageEventHandler,
      SessionDeleted: noopHandler,
      EventUndone: noopHandler,
      EventRedone: noopHandler,
      PlayerIdMapped: noopHandler, // Player mappings should be handled in full replay
      PlayerIdUnmapped: noopHandler, // Player un-mapping should be handled in full replay
    };

    for (const event of replayableEvents) {
      processEventHandler(event, handlerByType);
    }
  });

  // Validate the replayed session matches the Session schema
  try {
    return SessionSchema.parse(replayedSession);
  } catch (error) {
    console.error('Invalid session after snapshot replay:', error);
    throw new Error(
      `Session snapshot replay produced invalid session: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Pure function to replay session events and build full Session object
 *
 * @remarks
 * This function implements event sourcing by replaying all session events in order
 * to reconstruct the complete session state. It handles:
 * - Player ID mappings (for merging/unmerging players)
 * - Round and player index resolution (for efficient lookups)
 * - Score transfers when players are merged/unmerged
 * - Event filtering (undo/redo handling via event-activity)
 *
 * @param events - Array of session events to replay
 * @returns Complete Session object reconstructed from events
 * @throws Error if SessionStarted event is not found or if replayed session is invalid
 */
export const replaySessionEvents = (events: SessionEvent[]): Session => {
  const { events: replayableEvents } = getReplayableSessionEvents(events);

  // SessionStarted event must be first
  const startedEvent = replayableEvents.find((e) => e.type === 'SessionStarted');
  if (!startedEvent || startedEvent.type !== 'SessionStarted') {
    throw new Error('SessionStarted event not found');
  }

  const { payload } = startedEvent;

  // Build initial session from SessionStarted
  const session: Session = {
    id: startedEvent.sessionId,
    name: payload.name,
    notes: payload.notes,
    tags: undefined,
    gameConfigSnapshot: payload.gameConfigSnapshot,
    players: [], // Will be populated from PlayerJoined events
    teams:
      payload.teamConfiguration?.teams?.map((t) => ({
        ...t,
        createdAt: startedEvent.timestamp,
        updatedAt: startedEvent.timestamp,
        deletedAt: undefined,
      })) ?? [],
    rounds: [],
    imageIds: [],
    spectators: [], // Will be populated from SpectatorJoined events
    isPublicSpectator: false,
    isPublicPlayerJoin: false,
    startedAt: startedEvent.timestamp,
    status: 'ongoing',
    currentRoundNumber: 0,
    createdAt: startedEvent.timestamp,
    updatedAt: startedEvent.timestamp,
    deletedAt: undefined,
    archivedAt: undefined,
  };

  const roundIndexById = new Map<RoundId, number>();
  const playerIndexById = new Map<PlayerId, number>();
  const playerIdMappings = new Map<PlayerId, PlayerId>();
  type PlayerMappingSnapshot = {
    sourcePlayerId: PlayerId;
    targetPlayerId: PlayerId;
    roundScoreAdjustments: Map<RoundId, number>;
    playerIndex?: number;
    previousDeletedAt?: number;
    previousUpdatedAt?: number;
  };
  const playerIdMappingSnapshots = new Map<string, PlayerMappingSnapshot>();

  /**
   * Resolve a player ID through the mapping chain to get the final target ID
   *
   * @remarks
   * This function follows the chain of player ID mappings to find the ultimate target.
   * It handles transitive mappings (A -> B -> C) and prevents infinite loops by tracking visited IDs.
   * Used when players are merged multiple times or when resolving player IDs for score lookups.
   *
   * @param playerId - Player ID to resolve
   * @returns Resolved player ID (final target in mapping chain, or original ID if not mapped)
   */
  const resolveMappedPlayerId = (playerId: PlayerId): PlayerId => {
    let currentId = playerId;
    const visited = new Set<PlayerId>();
    while (playerIdMappings.has(currentId)) {
      const nextId = playerIdMappings.get(currentId);
      if (!nextId || visited.has(nextId)) {
        break;
      }
      visited.add(nextId);
      currentId = nextId;
    }
    return currentId;
  };

  /**
   * Register a player ID mapping (source -> target)
   *
   * @remarks
   * This function registers a mapping from source to target player ID, resolving transitive mappings.
   * If any existing mappings point to the source, they are updated to point to the resolved target.
   * This ensures that all mappings always point to the ultimate target (e.g., A -> B -> C becomes A -> C, B -> C).
   *
   * @param source - Source player ID (to be merged into target)
   * @param target - Target player ID (destination of merge)
   */
  const registerPlayerMapping = (source: PlayerId, target: PlayerId) => {
    const resolvedTarget = resolveMappedPlayerId(target);
    playerIdMappings.set(source, resolvedTarget);
    for (const [candidateSource, mappedTarget] of playerIdMappings.entries()) {
      if (mappedTarget === source) {
        playerIdMappings.set(candidateSource, resolvedTarget);
      }
    }
  };

  const unregisterPlayerMapping = (source: PlayerId) => {
    playerIdMappings.delete(source);
  };

  // Handle session-level events to reduce cognitive complexity
  const handleSessionEvent = (draft: Session, event: SessionEvent): void => {
    switch (event.type) {
      case 'SessionNameChanged': {
        draft.name = event.payload.name;
        break;
      }

      case 'SessionNotesChanged': {
        draft.notes = event.payload.notes;
        break;
      }

      case 'SessionGameConfigChanged': {
        draft.gameConfigSnapshot = event.payload.gameConfigSnapshot;
        break;
      }

      case 'SessionTagAdded': {
        if (!draft.tags) {
          draft.tags = [];
        }
        if (!draft.tags.includes(event.payload.tag)) {
          draft.tags.push(event.payload.tag);
        }
        break;
      }

      case 'SessionTagRemoved': {
        if (draft.tags) {
          draft.tags = draft.tags.filter((tag) => tag !== event.payload.tag);
        }
        break;
      }

      case 'SessionPaused': {
        draft.status = 'paused';
        draft.pausedAt = event.timestamp;
        break;
      }

      case 'SessionResumed': {
        draft.status = 'ongoing';
        draft.pausedAt = undefined;
        break;
      }

      case 'SessionRestarted': {
        // Reset session state for restart
        draft.status = 'ongoing';
        draft.pausedAt = undefined;
        draft.endedAt = undefined;
        draft.currentRoundNumber = 0;
        draft.updatedAt = event.timestamp;
        break;
      }

      case 'SessionEnded': {
        draft.status = 'finished';
        draft.endedAt = event.timestamp;
        break;
      }

      case 'SessionArchived': {
        const archivedAt = event.payload.archivedAt ?? event.timestamp;
        draft.archivedAt = archivedAt;
        draft.updatedAt = Math.max(draft.updatedAt, archivedAt);
        break;
      }

      case 'SessionUnarchived': {
        draft.archivedAt = undefined;
        break;
      }

      case 'SessionWinnerSet': {
        const { payload } = event;
        draft.winner = payload.winnerIds.length === 1 ? payload.winnerIds[0] : undefined;
        break;
      }

      case 'SessionOwnershipClaimed': {
        // Session schema does not track ownership; handled by metadata layer.
        break;
      }

      default: {
        // Not a session-level event
        break;
      }
    }
  };

  // Replay all filtered events in order using a single produce call for efficiency
  const replayedSession = produce(session, (draft) => {
    /**
     * Resolve round index by ID with caching for performance
     *
     * @remarks
     * This function resolves a round ID to its index in the rounds array, using a cache for performance.
     * The cache is validated to ensure it's still correct (rounds array may have changed).
     * If the cached index is invalid, it searches the array and updates the cache.
     *
     * @param roundId - Round ID to resolve
     * @returns Round index if found, undefined otherwise
     */
    const resolveRoundIndex = (roundId: RoundId): number | undefined =>
      resolveRoundIndexHelper(roundId, draft.rounds, roundIndexById);

    /**
     * Resolve player index by ID with caching and mapping support
     *
     * @remarks
     * This function resolves a player ID to its index in the players array, handling player mappings.
     * It first resolves the mapped ID (in case the player was merged), then checks both the mapped ID
     * and original ID as candidates. Uses caching for performance and validates cached indices.
     *
     * @param playerId - Player ID to resolve (may be a mapped ID)
     * @returns Player index if found, undefined otherwise
     */
    const resolvePlayerIndex = (playerId: PlayerId): number | undefined =>
      resolvePlayerIndexHelper(playerId, draft.players, playerIndexById, resolveMappedPlayerId);
    /**
     * Type guard for PlayerIdMapped event
     */
    const isPlayerIdMappedEvent = (
      event: SessionEvent,
    ): event is Extract<SessionEvent, { type: 'PlayerIdMapped' }> => {
      return event.type === 'PlayerIdMapped';
    };

    /**
     * Type guard for PlayerIdUnmapped event
     */
    const isPlayerIdUnmappedEvent = (
      event: SessionEvent,
    ): event is Extract<SessionEvent, { type: 'PlayerIdUnmapped' }> => {
      return event.type === 'PlayerIdUnmapped';
    };

    /**
     * Apply player ID mapping event (merge players)
     *
     * @remarks
     * This function applies a PlayerIdMapped event, which merges two players:
     * 1. Registers the mapping from source to target
     * 2. Transfers all scores from source to target player across all rounds
     * 3. Marks source player as deleted (soft delete)
     * 4. Creates a snapshot for potential undo (unmapping)
     *
     * The snapshot stores the score adjustments and player state so that unmapping
     * can restore the original state.
     *
     * @param event - PlayerIdMapped event to apply
     */
    const applyPlayerIdMapped = (event: Extract<SessionEvent, { type: 'PlayerIdMapped' }>) => {
      const { sourcePlayerId, targetPlayerId } = event.payload;
      registerPlayerMapping(sourcePlayerId, targetPlayerId);

      const resolvedTargetId = resolveMappedPlayerId(targetPlayerId);
      const snapshot: PlayerMappingSnapshot = {
        sourcePlayerId,
        targetPlayerId: resolvedTargetId,
        roundScoreAdjustments: new Map(),
      };

      for (const round of draft.rounds) {
        const existingScore = round.scores[sourcePlayerId];
        if (typeof existingScore === 'number') {
          const currentTargetScore = round.scores[resolvedTargetId] ?? 0;
          round.scores[resolvedTargetId] = currentTargetScore + existingScore;
          delete round.scores[sourcePlayerId];
          snapshot.roundScoreAdjustments.set(round.id, existingScore);
        }
      }

      const sourcePlayerIndex = draft.players.findIndex((player) => player.id === sourcePlayerId);
      if (sourcePlayerIndex !== -1) {
        const sourcePlayer = draft.players[sourcePlayerIndex];
        snapshot.playerIndex = sourcePlayerIndex;
        snapshot.previousDeletedAt = sourcePlayer.deletedAt;
        snapshot.previousUpdatedAt = sourcePlayer.updatedAt;
        sourcePlayer.deletedAt = event.timestamp;
        sourcePlayer.updatedAt = event.timestamp;
      }

      const targetPlayerIndex = draft.players.findIndex((player) => player.id === resolvedTargetId);
      const resolvedIndex = targetPlayerIndex === -1 ? sourcePlayerIndex : targetPlayerIndex;
      if (typeof resolvedIndex === 'number' && resolvedIndex >= 0) {
        playerIndexById.set(resolvedTargetId, resolvedIndex);
        playerIndexById.set(sourcePlayerId, resolvedIndex);
      }

      playerIdMappingSnapshots.set(event.id, snapshot);
    };

    /**
     * Restore round scores from snapshot (undo score transfer)
     *
     * @remarks
     * This function restores scores that were transferred during player mapping.
     * It reverses the score transfer by subtracting from target and adding back to source.
     * Used when unmapping players to restore the original score distribution.
     *
     * @param snapshot - Player mapping snapshot containing score adjustments
     */
    const restoreRoundScoresForSnapshot = (snapshot: PlayerMappingSnapshot) => {
      for (const [roundId, transferredScore] of snapshot.roundScoreAdjustments.entries()) {
        const roundIndex = resolveRoundIndex(roundId);
        if (typeof roundIndex !== 'number') {
          continue;
        }

        const round = draft.rounds[roundIndex];
        const currentTargetScore = round.scores[snapshot.targetPlayerId] ?? 0;
        const adjustedTargetScore = currentTargetScore - transferredScore;
        if (adjustedTargetScore > 0) {
          round.scores[snapshot.targetPlayerId] = adjustedTargetScore;
        } else {
          delete round.scores[snapshot.targetPlayerId];
        }
        const existingSourceScore = round.scores[snapshot.sourcePlayerId] ?? 0;
        round.scores[snapshot.sourcePlayerId] = existingSourceScore + transferredScore;
      }
    };

    /**
     * Restore source player state from snapshot (undo player deletion)
     *
     * @remarks
     * This function restores the source player's state that was changed during mapping.
     * It restores the deletedAt timestamp (soft delete) and updates the updatedAt timestamp.
     * Used when unmapping players to restore the original player state.
     *
     * @param snapshot - Player mapping snapshot containing player state
     * @param event - PlayerIdUnmapped event (for timestamp)
     */
    const restoreSourcePlayerState = (
      snapshot: PlayerMappingSnapshot,
      event: Extract<SessionEvent, { type: 'PlayerIdUnmapped' }>,
    ) => {
      if (snapshot.playerIndex === undefined) {
        playerIndexById.delete(snapshot.sourcePlayerId);
        return;
      }

      const sourcePlayer = draft.players[snapshot.playerIndex];
      if (sourcePlayer?.id === snapshot.sourcePlayerId) {
        sourcePlayer.deletedAt = snapshot.previousDeletedAt;
        sourcePlayer.updatedAt = event.timestamp;
      }
      playerIndexById.set(snapshot.sourcePlayerId, snapshot.playerIndex);
    };

    /**
     * Apply player ID unmapping event (unmerge players)
     *
     * @remarks
     * This function applies a PlayerIdUnmapped event, which reverses a player merge:
     * 1. Unregisters the mapping from source to target
     * 2. Restores scores from target back to source player
     * 3. Restores source player state (removes soft delete)
     * 4. Removes the snapshot (mapping is undone)
     *
     * This is the inverse operation of applyPlayerIdMapped and uses the snapshot
     * created during mapping to restore the original state.
     *
     * @param event - PlayerIdUnmapped event to apply
     */
    const applyPlayerIdUnmapped = (event: Extract<SessionEvent, { type: 'PlayerIdUnmapped' }>) => {
      const snapshot = playerIdMappingSnapshots.get(event.payload.originalMappingEventId);
      unregisterPlayerMapping(event.payload.sourcePlayerId);

      if (!snapshot) {
        playerIdMappingSnapshots.delete(event.payload.originalMappingEventId);
        return;
      }

      restoreRoundScoresForSnapshot(snapshot);
      restoreSourcePlayerState(snapshot, event);

      playerIdMappingSnapshots.delete(event.payload.originalMappingEventId);
    };

    const sessionEventHandler = (event: SessionEvent) => handleSessionEvent(draft, event);
    const playerEventHandler = (event: SessionEvent) =>
      handlePlayerEvent(draft, event, resolvePlayerIndex, playerIndexById);
    const roundEventHandler = (event: SessionEvent) =>
      handleRoundEvent(draft, event, resolveRoundIndex, roundIndexById);
    const scoreEventHandler = (event: SessionEvent) =>
      handleScoreEvent(draft, event, resolveRoundIndex, resolveMappedPlayerId);
    const spectatorEventHandler = (event: SessionEvent) => handleSpectatorEvent(draft, event);
    const inviteTokenEventHandler = (event: SessionEvent) => handleInviteTokenEvent(draft, event);
    const joinRequestEventHandler = (event: SessionEvent) => handleJoinRequestEvent(draft, event);
    const visibilityEventHandler = createVisibilityEventHandler(draft);
    const imageEventHandler = (event: SessionEvent) => handleImageEvent(draft, event);

    const handlerByType: Partial<Record<SessionEvent['type'], (event: SessionEvent) => void>> = {
      SessionNameChanged: sessionEventHandler,
      SessionNotesChanged: sessionEventHandler,
      SessionGameConfigChanged: sessionEventHandler,
      SessionTagAdded: sessionEventHandler,
      SessionTagRemoved: sessionEventHandler,
      SessionPaused: sessionEventHandler,
      SessionResumed: sessionEventHandler,
      SessionRestarted: sessionEventHandler,
      SessionArchived: sessionEventHandler,
      SessionUnarchived: sessionEventHandler,
      SessionEnded: sessionEventHandler,
      SessionWinnerSet: sessionEventHandler,
      SessionOwnershipClaimed: sessionEventHandler,
      PlayerJoined: playerEventHandler,
      PlayerLeft: playerEventHandler,
      PlayerRemoved: playerEventHandler,
      PlayerOrderChanged: playerEventHandler,
      SpectatorJoined: spectatorEventHandler,
      SpectatorLeft: spectatorEventHandler,
      RoundStarted: roundEventHandler,
      RoundCompleted: roundEventHandler,
      RoundNoteAdded: roundEventHandler,
      RoundNoteRemoved: roundEventHandler,
      RoundTagAdded: roundEventHandler,
      RoundTagRemoved: roundEventHandler,
      RoundDeleted: roundEventHandler,
      ScoreChanged: scoreEventHandler,
      ScoreSet: scoreEventHandler,
      InviteTokenGenerated: inviteTokenEventHandler,
      InviteTokenRevoked: inviteTokenEventHandler,
      InviteLinkEnabled: inviteTokenEventHandler,
      InviteLinkDisabled: inviteTokenEventHandler,
      SessionPublicSpectatorEnabled: visibilityEventHandler,
      SessionPublicSpectatorDisabled: visibilityEventHandler,
      SessionPublicPlayerJoinEnabled: visibilityEventHandler,
      SessionPublicPlayerJoinDisabled: visibilityEventHandler,
      JoinRequestCreated: joinRequestEventHandler,
      JoinRequestApproved: joinRequestEventHandler,
      JoinRequestRejected: joinRequestEventHandler,
      ImageAdded: imageEventHandler,
      ImageRemoved: imageEventHandler,
      ImageCaptionChanged: imageEventHandler,
      ImagePrimarySet: imageEventHandler,
      SessionDeleted: noopHandler,
      EventUndone: noopHandler,
      EventRedone: noopHandler,
      PlayerIdMapped: (event) => {
        if (isPlayerIdMappedEvent(event)) {
          applyPlayerIdMapped(event);
        }
      },
      PlayerIdUnmapped: (event) => {
        if (isPlayerIdUnmappedEvent(event)) {
          applyPlayerIdUnmapped(event);
        }
      },
    };

    for (const event of replayableEvents) {
      processEventHandler(event, handlerByType);
    }
  });

  // Validate the replayed session matches the Session schema
  // This ensures type safety matches runtime correctness
  try {
    return SessionSchema.parse(replayedSession);
  } catch (error) {
    console.error('Invalid session after replay:', error);
    throw new Error(
      `Session replay produced invalid session: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};
